import { ModalAction, ModalTitle } from '@overlayqa/shared/ui';
import { useEffect, useRef, useState } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import { Link } from 'react-router-dom';
import { LockedIcon } from '@hugeicons/core-free-icons';
import { setUpgradeInterceptor, type UpgradeBlock } from '@/lib/api/client';
import { capture } from '@/lib/analytics';
import { ModalPortal } from '@/components/shared/ModalPortal';

/**
 * In-context upgrade modal. Renders when an API request is blocked by a
 * plan/trial gate (403). The trigger is registered with the API client via
 * setUpgradeInterceptor, so any blocked action anywhere in the dashboard
 * surfaces this modal — instead of a silent failure or generic error toast.
 *
 * Mounted once in Layout.
 */

interface ReasonCopy {
  title: (b: UpgradeBlock) => string;
  body: (b: UpgradeBlock) => string;
  cta: string;
}

const COPY: Record<UpgradeBlock['reason'], ReasonCopy> = {
  trial_expired: {
    title: () => 'Your trial has ended',
    body: () =>
      'Your work is saved. Upgrade to keep capturing issues, running audits, and exporting.',
    cta: 'See plans',
  },
  plan_limit: {
    title: () => 'Plan limit reached',
    body: (b) => b.message || 'You have reached your plan limit. Upgrade for more headroom.',
    cta: 'Compare plans',
  },
  pro_feature: {
    title: (b) => `${b.feature ?? 'This'} is a Pro feature`,
    body: (b) => `Upgrade to Pro to use ${b.feature ?? 'this feature'}.`,
    cta: 'Upgrade to Pro',
  },
};

export function UpgradeModal() {
  const [block, setBlock] = useState<UpgradeBlock | null>(null);

  // Register as the API client's upgrade interceptor for this component's life.
  useEffect(() => {
    setUpgradeInterceptor((b) => {
      setBlock(b);
      capture('upgrade_modal_shown', { reason: b.reason, feature: b.feature });
    });
    return () => setUpgradeInterceptor(null);
  }, []);

  // Escape to dismiss, plus focus trapping and restore.
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(Boolean(block), dialogRef, () => setBlock(null));

  if (!block) return null;

  const copy = COPY[block.reason];
  const dismiss = () => setBlock(null);

  const overlay = (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden flex flex-col max-h-[calc(100dvh-var(--oq-space-8))]">
        {/* Header */}
        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between shrink-0">
          <ModalTitle icon={LockedIcon} id="upgrade-modal-title" className="oq-dialog__title">
              {copy.title(block)}
            </ModalTitle>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 shrink-0"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pt-4 pb-5 min-h-0 overflow-y-auto">
          <p className="modal-intro mb-5">{copy.body(block)}</p>

          <div className="flex flex-wrap gap-3 justify-end">
            <button type="button" className="btn-secondary whitespace-nowrap" onClick={dismiss}><ModalAction kind="cancel">
              Not now
            </ModalAction></button>
            <Link
              to="/billing"
              className="btn-primary whitespace-nowrap"
              onClick={() => {
                capture('upgrade_modal_cta_clicked', { reason: block.reason });
                dismiss();
              }}
            ><ModalAction kind="next">
              {copy.cta}
            </ModalAction></Link>
          </div>
        </div>
      </div>
    </div>
  );

  return <ModalPortal>{overlay}</ModalPortal>;
}
